export type ExpenseType = {
  id: string;
  title: string;
  note?: string;
  active: boolean;
};

export type ProjectOption = {
  id: string;
  code: string;
  name: string;
};

export type ExpenseEntry = {
  id: string;
  projectId: string | number;
  beneficiaryId: string;
  beneficiary: string;
  expenseTypeId: string;
  amount: number;
  dateFa: string;
  /** سند ثبت‌شده در ماژول حسابداری (در مقابل mock محلی) */
  source?: 'mock' | 'accounting' | 'bsi_bank' | 'advance';
  documentId?: string;
  docNo?: string;
  accountCode?: string;
  accountTitle?: string;
  debit?: number;
  credit?: number;
  /**
   * obligation: ثبت علی‌الحساب بدون انتقال بانکی (موجودی بانکی شرکت عوض نمی‌شود)
   * paid: پرداخت واقعی؛ اگر روش «بانک» باشد از حساب مبدأ کم می‌شود، حساب مقصد طرف برای ثبت اطلاعات پرداخت
   */
  recordingMode?: 'obligation' | 'paid';
  paymentMethod: 'نقدی' | 'بانک' | 'تنخواه';
  sourceBankAccountId?: number | null;
  destinationBankAccountId?: number | null;
  description: string;
  attachmentName?: string;
  attachmentDataUrl?: string;
};

const TYPES_KEY = 'accounting_mock_expense_types_v1';
const ENTRIES_KEY = 'accounting_mock_expense_entries_v1';

const DEFAULT_TYPES: ExpenseType[] = [
  { id: 't1', title: 'حقوق و دستمزد', note: 'پرسنل مستقیم پروژه', active: true },
  { id: 't2', title: 'خرید مصالح', note: 'سیمان، میلگرد، شن و ماسه', active: true },
  { id: 't3', title: 'پرداخت پیمانکار جزء', note: 'صورت‌کار جزء', active: true },
  { id: 't4', title: 'حمل و ماشین‌آلات', note: 'کرایه و سوخت', active: true },
];

const DEFAULT_PROJECTS: ProjectOption[] = [
  { id: 'p1', code: 'PRJ-401', name: 'خط انتقال آب شرق' },
  { id: 'p2', code: 'PRJ-402', name: 'ساختمان اداری داروگ' },
  { id: 'p3', code: 'PRJ-403', name: 'محوطه‌سازی فاز توسعه' },
];

const DEFAULT_ENTRIES: ExpenseEntry[] = [
  {
    id: 'e1',
    projectId: 'p1',
    beneficiaryId: 'company-1',
    beneficiary: 'شرکت بتن سازه پارس',
    expenseTypeId: 't2',
    amount: 285_000_000,
    dateFa: '1405/02/03',
    paymentMethod: 'بانک',
    sourceBankAccountId: null,
    destinationBankAccountId: null,
    description: 'خرید بتن آماده فاز دوم',
    attachmentName: '',
    attachmentDataUrl: '',
  },
  {
    id: 'e2',
    projectId: 'p2',
    beneficiaryId: 'employee-1',
    beneficiary: 'آقای حیدری',
    expenseTypeId: 't1',
    amount: 162_000_000,
    dateFa: '1405/02/02',
    paymentMethod: 'بانک',
    sourceBankAccountId: null,
    destinationBankAccountId: null,
    description: 'حقوق نیروهای اجرایی فروردین',
    attachmentName: '',
    attachmentDataUrl: '',
  },
];

function readJson<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJson<T>(key: string, value: T): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(key, JSON.stringify(value));
}

function stripAttachmentPayload(list: ExpenseEntry[]): ExpenseEntry[] {
  return list.map((row) => ({
    ...row,
    attachmentDataUrl: '',
  }));
}

export function getMockProjects(): ProjectOption[] {
  return DEFAULT_PROJECTS;
}

export function getExpenseTypes(): ExpenseType[] {
  return readJson<ExpenseType[]>(TYPES_KEY, DEFAULT_TYPES);
}

export function saveExpenseTypes(list: ExpenseType[]): void {
  writeJson(TYPES_KEY, list);
}

export function getExpenseEntries(): ExpenseEntry[] {
  const rows = readJson<ExpenseEntry[]>(ENTRIES_KEY, DEFAULT_ENTRIES);
  // Migration: old records may include heavy base64 payloads.
  if (rows.some((r) => (r.attachmentDataUrl || '').length > 0)) {
    const sanitized = stripAttachmentPayload(rows);
    try {
      writeJson(ENTRIES_KEY, sanitized);
    } catch {
      // ignore; at least return sanitized in memory
    }
    return sanitized;
  }
  return rows;
}

export function saveExpenseEntries(list: ExpenseEntry[]): void {
  // Keep metadata in persisted mock storage, avoid quota overflow by not persisting base64 data.
  const sanitized = stripAttachmentPayload(list);
  try {
    writeJson(ENTRIES_KEY, sanitized);
  } catch {
    // If browser storage is full, fallback to latest defaults and retry once.
    try {
      writeJson(ENTRIES_KEY, stripAttachmentPayload(DEFAULT_ENTRIES));
      writeJson(ENTRIES_KEY, sanitized);
    } catch {
      // swallow to keep UI responsive in mock mode
    }
  }
}
